import React, { useEffect, useState } from 'react';
import { useWorkflows } from '../hooks/use-workflows';
import { WorkflowCard, WorkflowEditor, WorkflowRunsDashboard } from '../components/workflows';
import type { Workflow, WorkflowStep, CreateWorkflowInput } from '../lib/workflow-types';

const STEP_TYPES = [
  { value: 'trigger', label: 'Trigger', description: 'Starts the workflow' },
  { value: 'action', label: 'Action', description: 'Performs an action' },
  { value: 'condition', label: 'Condition', description: 'Branching logic' },
  { value: 'transform', label: 'Transform', description: 'Data transformation' },
  { value: 'delay', label: 'Delay', description: 'Wait/delay step' },
];

const TRIGGER_TYPES = [
  { value: 'manual', label: 'Manual', description: 'Trigger manually' },
  { value: 'schedule', label: 'Schedule', description: 'Run on cron schedule' },
  { value: 'webhook', label: 'Webhook', description: 'Trigger via webhook' },
  { value: 'agent_event', label: 'Agent Event', description: 'Trigger on agent event' },
];

export function WorkflowBuilderPage() {
  const { workflows, runs, loading, error, fetchWorkflows, fetchRuns, createWorkflow, editWorkflow, deleteWorkflow, triggerWorkflow } = useWorkflows();
  const [editingWorkflow, setEditingWorkflow] = useState<Workflow | null>(null);
  const [view, setView] = useState<'list' | 'create' | 'runs'>('list');
  const [showRuns, setShowRuns] = useState<string | null>(null);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const handleCreate = async (data: CreateWorkflowInput) => {
    await createWorkflow(data);
    setView('list');
  };

  const handleUpdate = async (data: CreateWorkflowInput) => {
    if (editingWorkflow) {
      await editWorkflow(editingWorkflow.id, data);
      setEditingWorkflow(null);
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this workflow?')) {
      await deleteWorkflow(id);
    }
  };

  const handleTrigger = async (id: string) => {
    if (confirm('Run this workflow now?')) {
      await triggerWorkflow(id);
      fetchRuns();
    }
  };

  if (loading && workflows.length === 0) return <div className="p-8 text-center">Loading...</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4">
          <h1 className="text-3xl font-bold text-gray-900">Workflow Orchestration</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 px-4">
        {/* Navigation tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setView('list')}
            className={`px-4 py-2 rounded-md ${view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            My Workflows ({workflows.length})
          </button>
          <button
            onClick={() => setView('create')}
            className={`px-4 py-2 rounded-md ${view === 'create' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            + Create Workflow
          </button>
          <button
            onClick={() => { setView('list'); setShowRuns('all'); }}
            className={`px-4 py-2 rounded-md ${showRuns === 'all' ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            Run History
          </button>
        </div>

        {view === 'create' && !editingWorkflow && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Create New Workflow</h2>
            <WorkflowEditor
              onSubmit={handleCreate}
              onCancel={() => setView('list')}
              loading={loading}
            />
          </div>
        )}

        {view === 'list' && !editingWorkflow && showRuns !== 'all' && (
          <>
            {workflows.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <p className="text-gray-500 mb-4">No workflows yet. Create your first workflow!</p>
                <button onClick={() => setView('create')} className="px-4 py-2 bg-blue-600 text-white rounded-md">
                  Create Workflow
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workflows.map(workflow => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    onEdit={setEditingWorkflow}
                    onDelete={handleDelete}
                    onTrigger={handleTrigger}
                    onViewRuns={() => { setShowRuns(workflow.id); }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {editingWorkflow && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Edit Workflow</h2>
            <WorkflowEditor
              initialData={editingWorkflow}
              onSubmit={handleUpdate}
              onCancel={() => setEditingWorkflow(null)}
              loading={loading}
            />
          </div>
        )}

        {showRuns && (
          <WorkflowRunsDashboard
            workflowId={showRuns === 'all' ? undefined : showRuns}
            workflows={workflows}
            onClose={() => setShowRuns(null)}
          />
        )}
      </main>
    </div>
  );
}
